// The journey's 5 phases / 8 steps — the single registry behind the
// JourneyRail (the app's contents page since the journey page retired):
// identity, routing, descriptions (rail tooltips), and the landing pick.

import {
  Lightbulb,
  TrendingUp,
  AlertTriangle,
  MessageSquare,
  Sparkles,
  Wrench,
  CheckCircle2,
  Presentation,
} from 'lucide-react';
import type { ViewMode } from '@/lib/view-mode';
import type { StepId, StepStatus } from '@/features/project/step-status';

export interface JourneyStep {
  id: StepId;
  step: number;
  title: string;
  description: string;
  help: string;
  icon: React.ComponentType<{ className?: string }>;
  view: ViewMode;
}

export interface JourneyPhase {
  name: string;
  steps: JourneyStep[];
}

export const JOURNEY_PHASES: JourneyPhase[] = [
  {
    name: 'Frame',
    steps: [
      {
        id: 'idea',
        step: 1,
        title: 'Idea capture',
        description: 'Your idea, who it\'s for, and the outcome you want to enable for them. Everything else builds on this.',
        help: 'Write down the idea, who it\'s for, and what it should make possible for them. You can type it in or talk it through with the coach. The rest of the journey tests this against real evidence, so rough is fine. You can come back and sharpen it any time.',
        icon: Lightbulb,
        view: 'idea',
      },
    ],
  },
  {
    name: 'Assess',
    steps: [
      {
        id: 'market',
        step: 2,
        title: 'Market research',
        description: 'Check what\'s already out there and whether people seem to want it.',
        help: 'Before talking to anyone, see what already exists. This step scans for competitors and signs of demand, and you can paste in anything you\'ve found yourself. What turns up here feeds the risk-check on your assumptions.',
        icon: TrendingUp,
        view: 'market',
      },
      {
        id: 'assumptions',
        step: 3,
        title: 'Assumptions',
        description: 'Spot what your idea takes for granted, and which of those assumptions could sink it.',
        help: 'Every idea rests on things you\'re taking on faith. This step lists them and sorts them by how important they are and how sure you can be. The risky ones are what your customer conversations should test first.',
        icon: AlertTriangle,
        view: 'assumptions',
      },
    ],
  },
  {
    name: 'Discover',
    steps: [
      {
        id: 'dialog',
        step: 4,
        title: 'Customer dialogs',
        description: 'Talk to potential customers. What they say becomes the evidence behind your idea.',
        help: 'Send short AI-led conversations to potential customers. Respondents talk about their work in their own words, and what they say lands on the graph as evidence. No customers yet is normal. Anyone who fits the audience you described counts.',
        icon: MessageSquare,
        view: 'dialog',
      },
      {
        id: 'synthesis',
        step: 5,
        title: 'Review & synthesis',
        description: 'Group what people told you into themes you can act on.',
        help: 'One conversation is an anecdote. This step groups what different people said into themes, so you can see which needs come up again and again. You choose which themes are worth keeping.',
        icon: Sparkles,
        view: 'synthesis',
      },
    ],
  },
  {
    name: 'Decide',
    steps: [
      {
        id: 'capabilities',
        step: 6,
        title: 'What to build',
        description: 'Decide what the product needs to do to meet the needs you found.',
        help: 'Now the needs are clear, decide what the product would have to do about them. The AI proposes capabilities, and you keep the ones worth building. This is the bridge from what customers need to what you\'d make.',
        icon: Wrench,
        view: 'capabilities',
      },
      {
        id: 'thesis',
        step: 7,
        title: 'Pivot or persevere',
        description: 'Three questions before the pitch: did customers confirm the problem, are you building what they asked for, and which of your assumptions held up.',
        help: 'The honest check before the pitch. Did customers confirm the problem you set out to solve? Are you building what they asked for, or what you guessed they\'d want? And which of your assumptions did the evidence confirm or refute? The AI reads the evidence and suggests an answer to each. You make the call.',
        icon: CheckCircle2,
        view: 'validation',
      },
    ],
  },
  {
    name: 'Deck',
    steps: [
      {
        id: 'deck',
        step: 8,
        title: 'Pitch deck',
        description: 'A pitch deck built from your evidence, ending in a build, revise or stop call.',
        help: 'The end of the journey: a pitch deck built from your evidence, with a recommendation to build, revise, or stop. Its numbers and quotes are drawn from the graph, so you can defend each slide.',
        icon: Presentation,
        view: 'pitch-deck',
      },
    ],
  },
];

// Flat, journey-ordered step list.
export const JOURNEY_STEP_LIST: JourneyStep[] = JOURNEY_PHASES.flatMap((p) => p.steps);

// Keyed lookup for surfaces that address steps individually.
export const JOURNEY_STEPS = Object.fromEntries(
  JOURNEY_STEP_LIST.map((s) => [s.id, s]),
) as Record<StepId, JourneyStep>;

// Where opening a project lands: the first step the PM hasn't finished —
// deterministic "here's where we are". A fully-done journey lands on the deck
// (the terminal artefact, regenerate-able).
export function firstNotDoneStep(statuses: Record<StepId, StepStatus>): JourneyStep {
  return (
    JOURNEY_STEP_LIST.find((s) => statuses[s.id] !== 'done') ??
    JOURNEY_STEP_LIST[JOURNEY_STEP_LIST.length - 1]
  );
}

// The first step after `id` in journey order that isn't done yet; null when
// everything after it is done (or `id` is terminal/unknown). "Next" answers
// "what should I do now?", so a revisited journey skips past completed steps.
export function nextNotDoneStep(
  id: StepId,
  statuses: Record<StepId, StepStatus>,
): JourneyStep | null {
  const i = JOURNEY_STEP_LIST.findIndex((s) => s.id === id);
  if (i === -1) return null;
  return JOURNEY_STEP_LIST.slice(i + 1).find((s) => statuses[s.id] !== 'done') ?? null;
}
